import { KeyboardEvent, useCallback, useMemo } from "react";

import isHotkey from "is-hotkey";
import type { Descendant } from "slate";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import type { RenderElementProps, RenderLeafProps } from "slate-react";
import { Editable, Slate, withReact } from "slate-react";

import { BlockButton, Element, Leaf, MarkButton, Toolbar } from "./components";
import { CustomTextKey } from "./custom-types";
import styles from "./TextEditor.scss";
import { toggleMark } from "./utils";

const PLACEHOLDER = "Enter some text...";

const HOTKEYS: Record<string, CustomTextKey> = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

const initialValueNormaliser = (value: string): Descendant[] => {
  if (!value) {
    return [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ];
  }
  try {
    const parsedValue = JSON.parse(value);
    const isNonEmptyArray =
      Array.isArray(parsedValue) && parsedValue.length !== 0;
    if (parsedValue && isNonEmptyArray) {
      return parsedValue;
    }
  } catch {}

  return [
    {
      type: "paragraph",
      children: [{ text: value }],
    },
  ];
};
interface TextEditorProps {
  initialValue: string;
  readOnly?: boolean;
  dataTestId?: string;
  /**
   * Callback invoked when the editor content changes.
   * @param value - Array of Slate.js Descendant nodes representing the editor content structure.
   * See https://docs.slatejs.org/concepts/02-nodes for more details on the Slate document model.
   */
  onChange?: (value: Descendant[]) => void;
}

export const TextEditor = ({
  initialValue: initialValueProp,
  readOnly,
  dataTestId,
  onChange,
}: TextEditorProps) => {
  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} />,
    []
  );
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    []
  );
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  const initialValue = useMemo(
    () => initialValueNormaliser(initialValueProp || ""),
    [initialValueProp]
  );

  return (
    <div
      className={[styles.editor, readOnly ? styles.readonly : ""].join(" ")}
      data-test-id={dataTestId}
    >
      <Slate editor={editor} initialValue={initialValue} onChange={onChange}>
        {!readOnly && (
          <Toolbar>
            <MarkButton format="bold" icon="bold" />
            <MarkButton format="italic" icon="italic" />
            <MarkButton format="underline" icon="underline" />
            <MarkButton format="code" icon="code" />
            <BlockButton format="heading-one" label="H1" />
            <BlockButton format="heading-two" label="H2" />
            <BlockButton format="block-quote" icon="quote-right" />
            <BlockButton format="numbered-list" icon="list-numbered" />
            <BlockButton format="bulleted-list" icon="list-bullet" />
            <BlockButton format="left" icon="align-left" />
            <BlockButton format="center" icon="align-center" />
            <BlockButton format="right" icon="align-right" />
            <BlockButton format="justify" icon="align-justify" />
          </Toolbar>
        )}
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={PLACEHOLDER}
          spellCheck
          readOnly={readOnly}
          style={{
            minHeight: 120,
            maxHeight: 400,
            overflowX: "hidden",
            overflowY: "auto",
          }}
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                toggleMark(editor, mark);
              }
            }
          }}
        />
      </Slate>
    </div>
  );
};
